import ClearIcon from "@/assets/images/icons/clear-icon.svg";
import ClockIcon from "@/assets/images/icons/clock-icon.svg";
import SearchCloseIcon from "@/assets/images/icons/search-close-icon.svg";
import { InlineBrandSpinner } from "@/components/ui/InlineBrandSpinner";
import { SearchInputField } from "@/components/ui/SearchInputField";
import { SectionTitle } from "@/components/ui/Typography";
import { useEffectiveMinStatus } from "@/hooks/useDraftViewer";
import { useSearchTracking } from "@/hooks/useSearchTracking";
import { useAuth } from "@/lib/AuthContext";
import {
  GLOBAL_SEARCH_PLACEHOLDER_DESKTOP,
  GLOBAL_SEARCH_PLACEHOLDER_MOBILE,
} from "@/lib/courseSearchPlaceholder";
import { logger } from "@/lib/logger";
import { useIsMobile } from "@/lib/useDeviceType";
import type { GlobalSearchItem } from "@/supabase/services/globalSearch";
import { searchLessonsAndCourses } from "@/supabase/services/globalSearch";
import { router } from "expo-router";
import React, { useEffect, useMemo, useState } from "react";
import { FlatList, Platform, Pressable, ScrollViewProps, View } from "react-native";
import { SizableText, XStack, YStack, useMedia } from "tamagui";
import {
  DEFAULT_TIER_PICK,
  MAX_RECENT,
  MIN_SEARCH_QUERY_LENGTH,
  SEARCH_DEBOUNCE_MS,
  SEARCH_RESULTS_LIMIT,
  SEARCH_THUMB_COL_WIDTH_MOBILE,
  SEARCH_THUMB_COL_WIDTH_SM_UP,
  type TierPickState
} from "./globalSearchConstants";
import { loadGlobalSearchRecent, persistGlobalSearchRecent } from "./globalSearchRecent";
import { GlobalSearchResultRow, type GlobalSearchResultRowItem } from "./GlobalSearchResultRow";
import { SkillLevelFilterToolbar, computeSkillLevelsForRpc } from "./skillLevelFilter";

type GlobalSearchPanelProps = {
  open?: boolean;
  autoFocusInput?: boolean;
  onSelectQuery?: (q: string) => void;
  onClose?: () => void;
  showInlineCloseButton?: boolean;
  renderScrollComponent?: (props: ScrollViewProps) => React.ReactElement<ScrollViewProps>;
};

type SearchResultType = "course" | "lesson" | "collection";

type SearchResultItem = GlobalSearchResultRowItem;

type FilterPillProps = {
  label: string;
  count: number;
  active: boolean;
  onPress: () => void;
};

function FilterPill({ label, count, active, onPress }: FilterPillProps) {
  const [hovered, setHovered] = useState(false);
  const isHighlighted = active || hovered;

  return (
    <Pressable
      onPress={onPress}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      <XStack
        height={40}
        px={16}
        py={8}
        ai="center"
        jc="center"
        br={6}
        bg={isHighlighted ? "#0D0D0D" : "white"}
        borderWidth={1}
        borderColor={isHighlighted ? "#000000" : "#D2CCC5"}
      >
        <XStack ai="center" gap="$2">
          <SizableText
            fontSize={14}
            lineHeight={20}
            fontWeight="500"
            textTransform="capitalize"
            color={isHighlighted ? "#FFFFFF" : "#0D0D0D"}
          >
            {label}
          </SizableText>
          <YStack
            height={20}
            minWidth={20}
            px={2}
            ai="center"
            jc="center"
            br={6}
            bg={isHighlighted ? "rgba(255,255,255,0.2)" : "#F3F0EC"}
          >
            <SizableText
              fontSize={14}
              lineHeight={20}
              fontWeight="500"
              color={isHighlighted ? "#FFFFFF" : "#0D0D0D"}
              textAlign="center"
            >
              {count}
            </SizableText>
          </YStack>
        </XStack>
      </XStack>
    </Pressable>
  );
}

function SearchResultSeparator() {
  return <YStack height={12} />;
}

export function GlobalSearchPanel({
  open,
  autoFocusInput = false,
  onSelectQuery,
  onClose,
  showInlineCloseButton = true,
  renderScrollComponent,
}: GlobalSearchPanelProps) {
  const { user } = useAuth();
  // Allowlisted draft viewers also search non-published content; everyone else
  // is limited to Published (the RPC's default).
  const minStatus = useEffectiveMinStatus();
  const media = useMedia();
  const isMobile = useIsMobile();
  const searchThumbColW = media.gtSm ? SEARCH_THUMB_COL_WIDTH_SM_UP : SEARCH_THUMB_COL_WIDTH_MOBILE;

  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState<string[]>([]);
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [activeFilter, setActiveFilter] = useState<"all" | SearchResultType>("all");
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [tierPick, setTierPick] = useState<TierPickState>(() => ({ ...DEFAULT_TIER_PICK }));
  const [skillMenuOpen, setSkillMenuOpen] = useState(false);
  const [resultsListHeight, setResultsListHeight] = useState(0);
  const searchTracking = useSearchTracking({ scope: "global" });

  useEffect(() => {
    if (open === false) {
      setTierPick({ ...DEFAULT_TIER_PICK });
      setSkillMenuOpen(false);
    }
  }, [open]);

  useEffect(() => {
    let cancelled = false;
    void loadGlobalSearchRecent().then((items) => {
      if (!cancelled) setRecent(items);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const commitQuery = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const without = recent.filter((q) => q.toLowerCase() !== trimmed.toLowerCase());
    const next = [trimmed, ...without].slice(0, MAX_RECENT);
    setRecent(next);
    void persistGlobalSearchRecent(next);
    onSelectQuery?.(trimmed);
  };

  const handleSubmit = () => {
    commitQuery(query);
  };

  const handleKeyPress = (e: { key?: string; preventDefault?: () => void }) => {
    if (e?.key === "Enter") {
      e.preventDefault?.();
      handleSubmit();
    }
  };

  const handleRecentClick = (value: string) => {
    setQuery(value);
    commitQuery(value);
  };

  const handleResultPress = (item: SearchResultItem) => {
    const trimmed = query.trim();
    if (trimmed.length >= MIN_SEARCH_QUERY_LENGTH) {
      commitQuery(trimmed);
    }

    const position =
      filteredResults.findIndex(
        (r) => r.type === item.type && r.id === item.id,
      ) + 1;
    searchTracking.trackResultClick(
      trimmed,
      {
        id: item.id,
        type: item.type,
        slug: item.slug,
        title: item.title,
        courseSlug: item.courseSlug,
      },
      position,
    );

    if (item.type === "course") {
      if (item.slug) {
        router.push(`/courses/${item.slug}` as any);
      }
      onClose?.();
      return;
    }

    if (item.type === "collection" && item.courseSlug && item.slug) {
      router.push({
        pathname: "/courses/[course-slug]/collections/[collection-slug]",
        params: {
          "course-slug": item.courseSlug,
          "collection-slug": item.slug,
        },
      } as any);
      onClose?.();
      return;
    }

    if (item.courseSlug && item.slug) {
      router.push(`/courses/${item.courseSlug}/${item.slug}` as any);
      onClose?.();
    }
  };

  const filteredResults = useMemo(() => {
    if (activeFilter === "all") return results;
    return results.filter((r) => r.type === activeFilter);
  }, [results, activeFilter]);

  const counts = useMemo(
    () => ({
      all: results.length,
      courses: results.filter((r) => r.type === "course").length,
      lessons: results.filter((r) => r.type === "lesson").length,
      collections: results.filter((r) => r.type === "collection").length,
    }),
    [results],
  );

  const showResults = query.trim().length > 0;

  const typeFilterPills = (
    <>
      <FilterPill
        label="All"
        count={counts.all}
        active={activeFilter === "all"}
        onPress={() => setActiveFilter("all")}
      />
      <FilterPill
        label="Courses"
        count={counts.courses}
        active={activeFilter === "course"}
        onPress={() => setActiveFilter("course")}
      />
      <FilterPill
        label="Lessons"
        count={counts.lessons}
        active={activeFilter === "lesson"}
        onPress={() => setActiveFilter("lesson")}
      />
      <FilterPill
        label="Collections"
        count={counts.collections}
        active={activeFilter === "collection"}
        onPress={() => setActiveFilter("collection")}
      />
    </>
  );

  useEffect(() => {
    const q = query.trim();

    if (!q || q.length < MIN_SEARCH_QUERY_LENGTH) {
      setResults([]);
      setIsSearching(false);
      setSearchError(false);
      return;
    }

    const controller = new AbortController();
    setIsSearching(true);
    setSearchError(false);

    const handle = setTimeout(() => {
      void (async () => {
        try {
          const levels = computeSkillLevelsForRpc(tierPick);
          const raw: GlobalSearchItem[] = await searchLessonsAndCourses(
            q,
            SEARCH_RESULTS_LIMIT,
            user?.id,
            levels,
            minStatus,
          );

          if (controller.signal.aborted) return;

          const mapped: SearchResultItem[] = raw.map((item) => ({
            id: String(item.id),
            type: item.type,
            title: item.title,
            short_title: item.short_title,
            summary: item.summary,
            thumbnailUrl: item.thumbnailUrl ?? "",
            slug: item.slug,
            courseSlug: item.courseSlug,
            courseTitle: item.courseTitle,
            week_number: item.week_number,
            main_work: item.main_work,
            main_work_title: item.main_work_title,
            skillLevel: item.skillLevel,
            progressPercentage: item.progressPercentage,
          }));

          setResults(mapped);
          searchTracking.onResultsSettled(q, mapped);
        } catch (err) {
          if (controller.signal.aborted) return;
          logger.error("Global search failed", err);
          setResults([]);
          setSearchError(true);
        } finally {
          if (!controller.signal.aborted) {
            setIsSearching(false);
          }
        }
      })();
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      controller.abort();
      clearTimeout(handle);
    };
  }, [query, user?.id, tierPick, minStatus, searchTracking]);

  const isWeb = Platform.OS === "web";

  return (
    <YStack
      padding={isWeb ? 24 : 8}
      gap={isWeb ? 20 : 8}
      bg="white"
      w="100%"
      f={1}
      maxWidth={isWeb ? 945 : undefined}
      alignSelf="stretch"
    >
      <XStack ai="center" gap="$3">
        <SearchInputField
          flex={1}
          isMobile={isMobile}
          value={query}
          onChangeText={setQuery}
          placeholder={
            isMobile
              ? GLOBAL_SEARCH_PLACEHOLDER_MOBILE
              : GLOBAL_SEARCH_PLACEHOLDER_DESKTOP
          }
          autoFocus={autoFocusInput}
          onKeyPress={handleKeyPress}
          onSubmitEditing={handleSubmit}
          containerProps={{ maxWidth: 624, marginRight: "auto" }}
          trailing={
            isSearching && query.length > 0 ? (
              <InlineBrandSpinner />
            ) : query.length > 0 ? (
              <Pressable
                onPress={() => setQuery("")}
                hitSlop={8}
                style={{ justifyContent: "center", alignItems: "center" }}
              >
                <ClearIcon width={16} />
              </Pressable>
            ) : null
          }
        />

        {onClose && showInlineCloseButton && (
          <Pressable onPress={onClose} hitSlop={8}>
            <SearchCloseIcon width={40} height={40} />
          </Pressable>
        )}
      </XStack>

      {showResults ? (
        <>
          <YStack position="relative" zIndex={30}>
            <SkillLevelFilterToolbar
              isMobile={isMobile}
              tierPick={tierPick}
              setTierPick={setTierPick}
              skillMenuOpen={skillMenuOpen}
              setSkillMenuOpen={setSkillMenuOpen}
            >
              {typeFilterPills}
            </SkillLevelFilterToolbar>
          </YStack>

          <YStack mt="$4" gap="$3" f={1} minHeight={0} overflow="hidden">
            <SectionTitle color="#0D0D0D">
              {`Search results for \"${query}\"`}
            </SectionTitle>
            {searchError ? (
              <SizableText size="$3" color="#5C5854">
                Couldn&apos;t load results. Try again.
              </SizableText>
            ) : null}

            <View
              style={{ flex: 1, minHeight: 0 }}
              onLayout={(e) => {
                if (!isWeb) setResultsListHeight(e.nativeEvent.layout.height);
              }}
            >
              <FlatList
                data={filteredResults}
                keyExtractor={(item) => `${item.type}-${item.id}`}
                style={
                  isWeb
                    ? { flex: 1, minHeight: 0 }
                    : resultsListHeight > 0
                      ? { height: resultsListHeight }
                      : { flex: 1, minHeight: 0 }
                }
              contentContainerStyle={{ paddingHorizontal: 6, paddingTop: 6, paddingBottom: 16 }}
              ItemSeparatorComponent={SearchResultSeparator}
              showsVerticalScrollIndicator={false}
              nestedScrollEnabled
              keyboardShouldPersistTaps="handled"
              keyboardDismissMode="on-drag"
              initialNumToRender={10}
              maxToRenderPerBatch={8}
              windowSize={7}
              removeClippedSubviews={false}
              renderScrollComponent={renderScrollComponent}
              renderItem={({ item }) => (
                <GlobalSearchResultRow
                  item={item}
                  onPress={() => handleResultPress(item)}
                  thumbColWidth={searchThumbColW}
                  isSmUp={media.gtSm}
                />
              )}
            />
            </View>
          </YStack>
        </>
      ) : (
        <>
          <YStack gap={8} mt="$2">
            <SectionTitle color="#0D0D0D">
              Recent Searches
            </SectionTitle>
            {recent.length === 0 ? (
              <SizableText size="$3" color="#5C5854">
                – None yet
              </SizableText>
            ) : (
              <YStack gap={4}>
                {recent.map((item, index) => (
                  <Pressable key={item} onPress={() => handleRecentClick(item)}>
                    <XStack ai="center" gap="$2">
                      {index === 0 && <ClockIcon width={16} height={16} />}
                      <SizableText size="$3" color="#0D0D0D">
                        {item}
                      </SizableText>
                    </XStack>
                  </Pressable>
                ))}
              </YStack>
            )}
          </YStack>

          {/* <YStack gap={8} mt="$4">
            <SizableText color="#0D0D0D" fontSize={16} lineHeight={24} fontWeight="600">
              Trending Topics
            </SizableText>
            <XStack flexWrap="wrap" gap="$2">
              {TRENDING_TOPIC_LABELS.map((label) => (
                <Pressable key={label} onPress={() => handleRecentClick(label)}>
                  <XStack
                    px={16}
                    py={8}
                    ai="center"
                    jc="center"
                    br={6}
                    borderWidth={1}
                    borderColor="rgba(210, 204, 197, 1)"
                    bg="white"
                  >
                    <SizableText
                      color="#0D0D0D"
                      fontSize={14}
                      lineHeight={20}
                      fontWeight="500"
                      textTransform="capitalize"
                    >
                      {label}
                    </SizableText>
                  </XStack>
                </Pressable>
              ))}
            </XStack>
          </YStack> */}
        </>
      )}
    </YStack>
  );
}
